import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db } from './firebase';

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  phone: string;
  email?: string;
  createdAt?: Date;
}

class UserService {
  private readonly collectionName = 'users';
  private readonly userCache = new Map<string, User | null>();

  // Convertir un document Firestore en objet User
  private convertFirestoreToUser(doc: any): User {
    const data = doc.data();
    
    return {
      id: doc.id,
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      phone: data.phone || '',
      email: data.email || '',
      createdAt: data.createdAt?.toDate() || new Date()
    };
  }

  // Récupérer un utilisateur par son ID
  async getUserById(userId: string): Promise<User | null> {
    try {
      if (this.userCache.has(userId)) {
        return this.userCache.get(userId) ?? null;
      }

      const userDoc = await getDoc(doc(db, this.collectionName, userId));
      
      if (!userDoc.exists()) {
        console.warn(`Utilisateur avec l'ID ${userId} non trouvé`);
        this.userCache.set(userId, null);
        return null;
      }

      const user = this.convertFirestoreToUser(userDoc);
      this.userCache.set(userId, user);
      return user;
    } catch (error) {
      console.error(`Erreur lors de la récupération de l'utilisateur ${userId}:`, error);
      return null;
    }
  }

  // Récupérer plusieurs utilisateurs par leurs IDs
  async getUsersByIds(userIds: string[]): Promise<Map<string, User>> {
    const users = new Map<string, User>();
    
    try {
      const uniqueIds = Array.from(
        new Set(userIds.filter((userId) => userId && userId.trim().length > 0))
      );
      const missingIds: string[] = [];

      uniqueIds.forEach((userId) => {
        const cachedUser = this.userCache.get(userId);
        if (cachedUser !== undefined) {
          if (cachedUser) {
            users.set(userId, cachedUser);
          }
          return;
        }
        missingIds.push(userId);
      });

      const chunkSize = 30;
      const chunks: string[][] = [];
      for (let index = 0; index < missingIds.length; index += chunkSize) {
        chunks.push(missingIds.slice(index, index + chunkSize));
      }

      const snapshots = await Promise.all(
        chunks.map((chunk) =>
          getDocs(
            query(
              collection(db, this.collectionName),
              where(documentId(), 'in', chunk)
            )
          ).then((snapshot) => ({ chunk, snapshot }))
        )
      );

      snapshots.forEach(({ chunk, snapshot }) => {
        const fetchedIds = new Set<string>();

        snapshot.docs.forEach((userDoc) => {
          const user = this.convertFirestoreToUser(userDoc);
          users.set(user.id, user);
          this.userCache.set(user.id, user);
          fetchedIds.add(user.id);
        });

        chunk.forEach((userId) => {
          if (!fetchedIds.has(userId)) {
            this.userCache.set(userId, null);
          }
        });
      });

      uniqueIds.forEach((userId) => {
        const user = this.userCache.get(userId);
        if (user) {
          users.set(userId, user);
        }
      });
    } catch (error) {
      console.error('Erreur lors de la récupération des utilisateurs:', error);
    }

    return users;
  }

  // Obtenir le nom complet d'un utilisateur
  getFullName(user: User): string {
    const firstName = user.firstName?.trim() || '';
    const lastName = user.lastName?.trim() || '';
    
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    }
    
    return 'Client inconnu';
  }

  // Obtenir le téléphone formaté d'un utilisateur
  getFormattedPhone(user: User): string {
    return user.phone?.trim() || 'Non disponible';
  }
}

const userService = new UserService();
export default userService;
